import React from 'react';
import {
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Icon,
  TextField,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Typography
} from '@material-ui/core';
import useAuthContext from '../../hooks/useAuthContext';
import { useStyles } from './styles';
import {
  extractApiErrorCode,
  extractApiErrorMessage,
  getAcceptedLocationStablesByFarmType,
  notifications,
  UserAdminErrorCode
} from '../../utils';
import { CreateUpdateUserViewModel } from '../../models/user';
import { validateEmail } from '../../utils';
import { useTranslation } from 'react-i18next';
import { EnumCheckList, LanguageSelect } from '../../elements';
import UserRole from '../../constants/user-role';
import { LocationStableTranslations } from '../../models/locations';
import { AcceptedDaysOfWeekOrdered, DayOfWeekTranslations } from '../../models/common';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainSettingsResultTranslationViewModel,
  HealthSystemDomainUsersCommandsUpdateUserCommand,
  HealthSystemDomainUsersResultUserViewModel
} from '../../api/HealthSystemApi';

/**
 * What the dialog edits. Editing passes a whole user; inviting passes `{}` or `{ farmId }`, and the missing id is
 * what switches the dialog to create mode.
 */
export type UserDraft = Partial<HealthSystemDomainUsersResultUserViewModel>;

interface IProps {
  opened: boolean;
  onModalAction: (result: boolean | HealthSystemDomainUsersResultUserViewModel | null) => void;
  returnObject: boolean;
  user: UserDraft;
  // Display name of the farm the invite targets. Falls back to the admin's own farm
  // (from auth state) when not provided, e.g. when inviting from a multi-farm admin screen.
  farmName?: string;
}

const EMPLOYEE_MIN_NAME_LENGTH = 3;

const CreateUpdateUserModal: React.FC<IProps> = (props) => {
  const { opened, onModalAction, returnObject, user: initialUser, farmName } = props;

  const classes = useStyles();
  const { state } = useAuthContext();
  const { t } = useTranslation();

  const apiService = new ApiService();

  const [translations, setTranslations] = React.useState<HealthSystemDomainSettingsResultTranslationViewModel[]>([]);
  const [loaded, setLoaded] = React.useState(false);

  const [isBusy, setIsBusy] = React.useState(false);
  const [nameError, setNameError] = React.useState(false);
  const [emailError, setEmailError] = React.useState(false);

  const [user, setUser] = React.useState<UserDraft>(initialUser);

  const editMode = !!user.id;
  const dialogTitle = editMode ? t('f-usr-upd-title') : t('f-usr-invite-title');
  const saveButtonTitle = editMode ? t('f-com-edit') : t('f-usr-invite-submit');
  const invitedFarmName = farmName || state.farmName || state.farmNumber || '';

  // Clear user fields when the modal opens
  React.useEffect(() => {
    if (opened) {
      setUser({ ...initialUser }); // Set user fields based on the initialUser prop
      if (!editMode) {
        clearFields(); // Clear fields if in add mode
      }
    }

    if (opened && !loaded) {
      loadTranslations();
    }
  }, [opened]);

  const loadTranslations = async () => {
    setIsBusy(true);

    apiService.api.settings
      .settingsGetTranslations()
      .then((result) => {
        setTranslations(result.data);
        setLoaded(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const closeDialog = (text: string | null) => {
    if (text == null) {
      onModalAction(null);
      return;
    }

    if (!validateForm()) {
      return;
    }

    if (!editMode) {
      if (returnObject) {
        onModalAction(buildUserObject());
      } else {
        handleInviteUser();
      }
    } else if (editMode) {
      handleUpdateUser();
    }
  };

  const handleInviteUser = () => {
    setIsBusy(true);

    apiService.api.userAccounts
      .userAccountsSendInvitation({
        name: user.name ?? '',
        email: user.email ?? '',
        role: user.role ?? '',
        languageIso: user.languageIso ?? 'EN',
        farmId: user.farmId ?? null,
        stables: user.stables ?? [],
        availability: user.availability ?? []
      })
      .then(() => {
        notifications.success(t('f-usr-invite-ok'));
        onModalAction(true);
      })
      .catch((e) => {
        // The address stays taken while the account is only deactivated, which reads as a broken
        // invitation unless the reactivation route is spelled out.
        if (extractApiErrorCode(e) === UserAdminErrorCode.DeactivatedEmailExists) {
          notifications.error(t('f-usr-invite-deactivated-exists'));
        } else {
          notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        }

        onModalAction(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const handleUpdateUser = () => {
    setIsBusy(true);

    const command: HealthSystemDomainUsersCommandsUpdateUserCommand = buildUserObject();

    apiService.api.users
      .usersUpdateUser(command)
      .then(() => {
        notifications.success(t('f-usr-upd-ok'));
        onModalAction(true);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
        onModalAction(false);
      })
      .finally(() => {
        setIsBusy(false);
      });
  };

  const buildUserObject = () => {
    return {
      id: user.id,
      name: user.name,
      email: user.email,
      stables: user.stables,
      availability: user.availability,
      role: user.role,
      farmId: user.farmId,
      languageIso: user.languageIso,
      // Passwords are set by the user via invitation/reset flows, never by an admin
      password: '',
      isActivated: user.isActivated,
      isDeactivated: user.isDeactivated
    } as CreateUpdateUserViewModel;
  };

  const clearFields = () => {
    setUser((prev) => ({
      ...prev,
      name: '',
      email: '',
      role: UserRole.user,
      languageIso: 'EN',
      stables: [],
      availability: []
    }));

    setNameError(false);
    setEmailError(false);
  };

  const validateForm = (): boolean => {
    let valid = true;

    const command = buildUserObject();

    if (!user) {
      notifications.warning(t('f-val-usr-email'));
      valid = false;
    }

    if (!command.name || command.name.length < EMPLOYEE_MIN_NAME_LENGTH) {
      setNameError(true);
      valid = false;
    } else {
      setNameError(false);
    }

    if (!command.email || command.email.length === 0) {
      notifications.warning(t('f-val-usr-email-valid'));
      setEmailError(true);
      valid = false;
    } else {
      if (!validateEmail(command.email)) {
        notifications.warning(t('f-val-usr-email-valid'));
        setEmailError(true);
        valid = false;
      } else {
        setEmailError(false);
      }
    }

    return valid;
  };

  const shouldDisableManagerRole = (): boolean => {
    return state.role === UserRole.user;
  };

  const shouldDisableAdminRole = (): boolean => {
    return state.role !== UserRole.admin;
  };

  return (
    <Dialog
      id="createUserDialog"
      open={opened}
      onClose={() => closeDialog(null)}
      className={classes.dialog}
      maxWidth="lg"
      aria-labelledby="form-dialog-title"
    >
      <DialogTitle id="createUserDialogTitle" className={classes.dialogHeaderFooter}>
        {dialogTitle}
      </DialogTitle>

      <DialogContent>
        <Grid item container direction="column">
          {!editMode && (
            <Grid item>
              <TextField
                id="inviteFarmName"
                label={t('f-usr-invite-farm')}
                size="small"
                fullWidth
                variant="outlined"
                className={classes.formControl}
                value={invitedFarmName}
                disabled
                InputProps={{ readOnly: true }}
              />
            </Grid>
          )}

          <Grid item>
            <TextField
              label={t('f-usr-name')}
              size="small"
              fullWidth
              variant="outlined"
              className={classes.formControl}
              value={user.name}
              onChange={(e) => setUser({ ...user, name: e.target.value })}
              error={nameError}
              helperText={nameError ? t('f-com-name-required') : ''}
            />
          </Grid>

          <Grid item>
            <TextField
              label={t('f-com-email')}
              size="small"
              fullWidth
              variant="outlined"
              type="email"
              className={classes.formControl}
              value={user.email}
              onChange={(e) => setUser({ ...user, email: e.target.value })}
              error={emailError}
              helperText={emailError ? t('f-com-email-required') : ''}
            />
          </Grid>

          {!editMode && (
            <Grid item>
              <p className={`${classes.formControl} text-sm text-gray-500 mb-2`}>{t('f-usr-invite-info')}</p>
            </Grid>
          )}

          <Grid item>
            <FormControl className={classes.formControl} fullWidth>
              <InputLabel id="role-lbl">{t('f-usr-role')}</InputLabel>
              <Select
                labelId="role-lbl"
                id="role-select"
                value={user.role}
                onChange={(e) => setUser({ ...user, role: e.target.value as string })}
              >
                <MenuItem value={'user'}>{t('f-user')}</MenuItem>
                <MenuItem value={'manager'} disabled={shouldDisableManagerRole()}>
                  {t('f-manager')}
                </MenuItem>
                <MenuItem value={'admin'} disabled={shouldDisableAdminRole()}>
                  {t('f-admin')}
                </MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item>
            <LanguageSelect
              cssClass={classes.formControl}
              disabled={!loaded}
              fullWidth
              translations={translations}
              value={loaded ? (user.languageIso ?? 'EN') : ''}
              onLanguageChange={(lng) => setUser({ ...user, languageIso: lng })}
            />
          </Grid>

          <Grid item>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Typography align="center">{t('f-employees-select-assigned')}</Typography>
                <EnumCheckList
                  onChange={(stables) => setUser({ ...user, stables })}
                  selectedValues={user.stables}
                  acceptedValues={getAcceptedLocationStablesByFarmType(state.farmType)}
                  translations={LocationStableTranslations}
                />
              </Grid>

              <Grid item xs={6}>
                <Typography align="center">{t('f-employees-select-availability')}</Typography>
                <EnumCheckList
                  onChange={(days) => setUser({ ...user, availability: days })}
                  selectedValues={user.availability}
                  acceptedValues={AcceptedDaysOfWeekOrdered}
                  translations={DayOfWeekTranslations}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>

      <DialogActions className={classes.dialogHeaderFooter}>
        {isBusy && <CircularProgress />}

        <Button
          onClick={() => closeDialog(null)}
          variant="outlined"
          disabled={isBusy}
          startIcon={<Icon>{'close'}</Icon>}
          color="primary"
        >
          {t('f-com-cancel')}
        </Button>

        <Button
          onClick={() => closeDialog('ok')}
          id="saveUserBtn"
          disabled={isBusy}
          startIcon={<Icon>{editMode ? 'save' : 'send'}</Icon>}
          variant="contained"
          color="primary"
        >
          {saveButtonTitle}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreateUpdateUserModal;
